import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { NUTRIENTS_UPDATED } from '@/hooks/useMeals';
import { format, startOfDay, endOfDay } from 'date-fns';
// ============================================
// ТИПЫ
// ============================================
export type NutrientKey =
  | 'protein'
  | 'fiber'
  | 'vegetables_fruits'
  | 'omega3'
  | 'saturated_fat'
  | 'calcium'
  | 'iron'
  | 'magnesium'
  | 'zinc'
  | 'potassium'
  | 'vitamin_d'
  | 'vitamin_b12'
  | 'vitamin_c';
export type NutrientStatus = 'good' | 'warning' | 'lack';
export interface NutrientTarget {
  nutrient_key: NutrientKey;
  amount: number;
  amount_max?: number | null;
  unit: string;
  target_type: 'standard' | 'range' | 'reverse' | 'weight_based';
  energy_dependent: boolean;
  source: string;
}
export interface NutrientData {
  key: NutrientKey;
  label: string;
  emoji: string;
  consumed: number;
  target: number;
  target_max?: number;
  unit: string;
  percentage: number;
  status: NutrientStatus;
  target_type: 'standard' | 'range' | 'reverse' | 'weight_based';
}
interface DailySummary {
  date: string;
  consumed: Record<string, number>;
  targets: Record<string, NutrientTarget>;
}
// ============================================
// КОНФИГ — ЛЕЙБЛЫ И ЭМОДЗИ
// ============================================
export const NUTRIENT_CONFIG: Record<NutrientKey, { label: string; emoji: string; consumedKey: string }> = {
  protein: { label: 'Белок', emoji: '🥩', consumedKey: 'protein_g' },
  fiber: { label: 'Клетчатка', emoji: '🌾', consumedKey: 'fiber_g' },
  vegetables_fruits: { label: 'Овощи и фрукты', emoji: '🥬', consumedKey: 'vegetables_fruits_g' },
  omega3: { label: 'Омега-3', emoji: '🐟', consumedKey: 'omega3_g' },
  saturated_fat: { label: 'Насыщенные жиры', emoji: '🥓', consumedKey: 'saturated_fat_g' },
  calcium: { label: 'Кальций', emoji: '🥛', consumedKey: 'calcium_mg' },
  iron: { label: 'Железо', emoji: '🩸', consumedKey: 'iron_mg' },
  magnesium: { label: 'Магний', emoji: '💪', consumedKey: 'magnesium_mg' },
  // Цинк добавлен 12.10.2026 (миграция 065): иммунитет, кожа, заживление
  zinc: { label: 'Цинк', emoji: '🛡️', consumedKey: 'zinc_mg' },
  potassium: { label: 'Калий', emoji: '🟠', consumedKey: 'potassium_mg' },
  vitamin_d: { label: 'Витамин D', emoji: '☀️', consumedKey: 'vitamin_d_mcg' },
  vitamin_b12: { label: 'Витамин B12', emoji: '💊', consumedKey: 'vitamin_b12_mcg' },
  vitamin_c: { label: 'Витамин C', emoji: '🍊', consumedKey: 'vitamin_c_mg' },
};
// Порядок отображения (в порядке важности)
// БЕЛОК — отслеживается на главном (кольцо КБЖУ), здесь не дублируем
export const NUTRIENT_ORDER: NutrientKey[] = [
  'fiber',
  'vegetables_fruits',
  'calcium',
  'iron',
  'magnesium',
  'zinc',
  'potassium',
  'omega3',
  'vitamin_d',
  'vitamin_b12',
  'vitamin_c',
  'saturated_fat',
];

// Единицы в базе норм записаны латиницей (g, mg, mcg) — на экране показываем по-русски.
const UNIT_RU: Record<string, string> = { g: 'г', mg: 'мг', mcg: 'мкг', ug: 'мкг', 'µg': 'мкг' };
export function unitRu(unit: string | null | undefined): string {
  const u = String(unit || 'g').trim();
  return UNIT_RU[u.toLowerCase()] ?? u;
}

// Нутриенты «безопасные сверху» — перебор не считается проблемой
// (овощи/фрукты, клетчатка — больше = хорошо, верхняя граница лишь рекомендация)
const SAFE_OVER_NUTRIENTS: Set<string> = new Set(['vegetables_fruits', 'fiber']);

// ============================================
// ВЫЧИСЛЕНИЕ СТАТУСА
// ============================================
/**
 * Определяет статус нутриента (good / warning / lack)
 * с учётом типа нормы (standard / range / reverse / weight_based)
 * 
 * @param key — ключ нутриента (нужен для special-case логики овощей/клетчатки)
 */
export function computeStatus(
  consumed: number,
  target: number,
  target_max: number | undefined,
  target_type: string,
  key: string
): NutrientStatus {
  if (target <= 0) return 'lack';
  // REVERSE — насыщенные жиры (меньше = лучше)
  if (target_type === 'reverse') {
    const ratio = consumed / target;
    if (ratio <= 0.8) return 'good';      // меньше 80% от лимита — отлично
    if (ratio <= 1.0) return 'warning';   // в пределах лимита
    return 'lack';                         // превышение — плохо
  }
  // RANGE — овощи/фрукты, клетчатка (есть оптимум)
  if (target_type === 'range' && target_max) {
    if (consumed >= target && consumed <= target_max) return 'good';   // в оптимуме
    
    // 🆕 ФИКС: для овощей/клетчатки превышение = тоже good
    // (нельзя «переесть» овощами в плохом смысле)
    if (consumed > target_max && SAFE_OVER_NUTRIENTS.has(key)) {
      return 'good';
    }
    
    // Для других range-нутриентов превышение = warning
    if (consumed > target_max) return 'warning';
    
    // Ниже минимума
    if (consumed >= target * 0.7) return 'warning';  // близко к минимуму
    return 'lack';
  }
  // STANDARD и WEIGHT_BASED — обычная норма (больше = лучше)
  const percentage = (consumed / target) * 100;
  if (percentage >= 80) return 'good';
  if (percentage >= 50) return 'warning';
  return 'lack';
}
// ============================================
// ХУК
// ============================================
/**
 * Получить сводку микронутриентов за день.
 * 
 * @example
 * const { nutrients, loading, refetch } = useDailyMicronutrients();
 * // nutrients - массив NutrientData (готовый для UI)
 * // loading - идёт ли загрузка
 * // refetch - функция обновить данные
 */
export function useDailyMicronutrients(date: Date = new Date()) {
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dateStr = format(date, 'yyyy-MM-dd');
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('get_daily_nutrients_summary', {
      p_date: dateStr,
    });
    if (rpcError) {
      console.error('[useDailyMicronutrients] error:', rpcError);
      setError(rpcError.message);
      setSummary(null);
    } else {
      setSummary(data as DailySummary);
    }
    setLoading(false);
  }, [dateStr]);
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Досчёт микронутриентов идёт на сервере уже после сохранения еды —
  // перечитываем, когда он закончился, иначе экран остаётся с теми цифрами,
  // что успел загрузить до досчёта
  useEffect(() => {
    const onUpdated = () => refetch();
    window.addEventListener(NUTRIENTS_UPDATED, onUpdated);
    return () => window.removeEventListener(NUTRIENTS_UPDATED, onUpdated);
  }, [refetch]);
  // Преобразуем данные в массив для UI
  const nutrients: NutrientData[] = (() => {
    if (!summary || !summary.consumed || !summary.targets) return [];
    return NUTRIENT_ORDER.map((key) => {
      const config = NUTRIENT_CONFIG[key];
      const targetData = summary.targets[key];
      // Если для этого нутриента нет нормы — пропускаем
      if (!targetData || (targetData as any).error) {
        return null;
      }
      const consumed = Number(summary.consumed[config.consumedKey] || 0);
      const target = Number(targetData.amount);
      const targetMax = targetData.amount_max ? Number(targetData.amount_max) : undefined;
      const targetType = targetData.target_type || 'standard';
      // % выполнения (для range берём процент от минимума, чтобы 100% = норма достигнута)
      let percentage: number;
      if (targetType === 'range' && targetMax) {
        // 🆕 ФИКС: процент считаем от МИНИМУМА (нижняя граница нормы)
        // Так юзер видит: 400г = 100%, 580г = 145%, 700г = 175%
        percentage = Math.min(200, Math.round((consumed / target) * 100));
      } else if (targetType === 'reverse') {
        // Для обратного — процент от лимита
        percentage = Math.min(150, Math.round((consumed / target) * 100));
      } else {
        percentage = Math.min(150, Math.round((consumed / target) * 100));
      }
      return {
        key,
        label: config.label,
        emoji: config.emoji,
        consumed: Math.round(consumed * 10) / 10,
        target: Math.round(target * 10) / 10,
        target_max: targetMax ? Math.round(targetMax * 10) / 10 : undefined,
        unit: unitRu(targetData.unit),
        percentage,
        status: computeStatus(consumed, target, targetMax, targetType, key),
        target_type: targetType as any,
      };
    }).filter(Boolean) as NutrientData[];
  })();
  // Вспомогательная статистика для UI
  const stats = {
    good: nutrients.filter((n) => n.status === 'good'),
    warning: nutrients.filter((n) => n.status === 'warning'),
    lack: nutrients.filter((n) => n.status === 'lack'),
    total: nutrients.length,
  };
  return {
    nutrients,
    stats,
    summary,
    loading,
    error,
    refetch,
  };
}

// ============================================
// ИСТОЧНИКИ — из каких продуктов набрался нутриент
// ============================================
/**
 * Микронутриенты пишутся не только в дневную сумму, но и в колонки каждого
 * продукта в meal_items (см. enrichMealNutrients в useMeals). Значит вклад
 * конкретной еды считается из уже имеющихся данных — без обращения к ИИ.
 */
export interface NutrientSource {
  name: string;
  amount: number;
  share: number; // доля в дневном итоге, %
}

const TOP_SOURCES = 3;

export function useNutrientSources(date: Date = new Date()) {
  const [sources, setSources] = useState<Record<string, NutrientSource[]>>({});
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const dateStr = format(date, 'yyyy-MM-dd');

  // Источники считаются из тех же колонок, что и итоги, — значит их тоже
  // надо перечитать, когда серверный досчёт закончился
  useEffect(() => {
    const onUpdated = () => setTick((t) => t + 1);
    window.addEventListener(NUTRIENTS_UPDATED, onUpdated);
    return () => window.removeEventListener(NUTRIENTS_UPDATED, onUpdated);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      const day = new Date(`${dateStr}T12:00:00`);

      // Двумя запросами вместо вложенного фильтра: так поведение не зависит
      // от тонкостей join-синтаксиса PostgREST
      const { data: meals } = await supabase
        .from('meals')
        .select('id')
        .gte('consumed_at', startOfDay(day).toISOString())
        .lte('consumed_at', endOfDay(day).toISOString());

      const ids = (meals ?? []).map((m: any) => m.id);
      if (!ids.length) {
        if (!cancelled) {
          setSources({});
          setLoading(false);
        }
        return;
      }

      const cols = Object.values(NUTRIENT_CONFIG).map((c) => c.consumedKey);
      const { data: items } = await supabase
        .from('meal_items')
        .select(['name', ...cols].join(', '))
        .in('meal_id', ids);

      if (cancelled) return;

      const result: Record<string, NutrientSource[]> = {};
      for (const key of Object.keys(NUTRIENT_CONFIG) as NutrientKey[]) {
        const col = NUTRIENT_CONFIG[key].consumedKey;

        // Один и тот же продукт мог встретиться в нескольких приёмах — складываем
        const byName = new Map<string, number>();
        for (const it of (items ?? []) as any[]) {
          const v = Number(it[col]) || 0;
          if (v <= 0) continue;
          const name = (it.name || '').trim();
          if (!name) continue;
          byName.set(name, (byName.get(name) ?? 0) + v);
        }

        const total = [...byName.values()].reduce((s, v) => s + v, 0);
        if (total <= 0) continue;

        const ranked = [...byName.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, TOP_SOURCES)
          .map(([name, amount]) => ({
            name,
            amount: Math.round(amount * 10) / 10,
            share: Math.round((amount / total) * 100),
          }));

        // Вклад меньше 5% — шум в строке, но главный источник оставляем всегда
        result[key] = ranked.filter((s, i) => i === 0 || s.share >= 5);
      }

      setSources(result);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [dateStr, tick]);

  return { sources, loading };
}

// ============================================
// ПЕРИОД — ретроспектива без обращения к ИИ
// ============================================
/**
 * Микронутриенты за период (06.10.2026). Раньше экран показывал только сегодня;
 * запрос Славы — неделя, месяц, прошлые месяцы, по дням и суммой.
 *
 * Данные уже лежат в meal_items с момента записи еды, так что это сложение
 * сохранённого (RPC get_nutrients_range, миграция 059), а не новый расчёт.
 *
 * ГЛАВНОЕ ПРАВИЛО: среднее считается по дням С ЗАПИСЯМИ. День без единого приёма —
 * это «не записал», а не «съел ноль»; иначе неделя с двумя пропусками показала бы
 * нехватку всего подряд.
 */
export interface PeriodDayPoint {
  date: string;          // yyyy-MM-dd
  value: number;
  status: NutrientStatus;
}
export interface PeriodNutrient {
  key: NutrientKey;
  label: string;
  emoji: string;
  unit: string;
  target: number;
  target_max?: number;
  target_type: NutrientData['target_type'];
  avg: number;            // в среднем за день с записями
  total: number;          // всего за период
  percentage: number;     // среднее от нормы, %
  status: NutrientStatus; // статус среднего
  daysGood: number;       // сколько дней было в норме
  series: PeriodDayPoint[];
}
interface RangeResponse {
  error?: string;
  from: string;
  to: string;
  first_day: string | null;
  days: { date: string; meals: number; consumed: Record<string, number> }[];
  targets: Record<string, NutrientTarget>;
}

export function useNutrientsRange(from: string, to: string) {
  const [data, setData] = useState<RangeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase.rpc('get_nutrients_range', { p_from: from, p_to: to }).then(({ data: res, error: rpcError }) => {
      if (cancelled) return;
      const failed = rpcError?.message || (res as RangeResponse | null)?.error || null;
      if (failed) {
        console.error('[useNutrientsRange] error:', failed);
        setError(failed);
        setData(null);
      } else {
        setData(res as RangeResponse);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [from, to]);

  const days = data?.days ?? [];
  const nutrients: PeriodNutrient[] = (() => {
    if (!data?.targets || !days.length) return [];
    return NUTRIENT_ORDER.map((key) => {
      const config = NUTRIENT_CONFIG[key];
      const t = data.targets[key];
      if (!t || (t as any).error) return null;
      const target = Number(t.amount);
      const targetMax = t.amount_max ? Number(t.amount_max) : undefined;
      const targetType = (t.target_type || 'standard') as NutrientData['target_type'];
      const series: PeriodDayPoint[] = days.map((d) => {
        const value = Number(d.consumed?.[config.consumedKey] || 0);
        return { date: d.date, value: Math.round(value * 10) / 10, status: computeStatus(value, target, targetMax, targetType, key) };
      });
      const total = series.reduce((a, p) => a + p.value, 0);
      const avg = total / series.length;
      return {
        key,
        label: config.label,
        emoji: config.emoji,
        unit: unitRu(t.unit),
        target: Math.round(target * 10) / 10,
        target_max: targetMax ? Math.round(targetMax * 10) / 10 : undefined,
        target_type: targetType,
        avg: Math.round(avg * 10) / 10,
        total: Math.round(total * 10) / 10,
        percentage: target > 0 ? Math.min(200, Math.round((avg / target) * 100)) : 0,
        status: computeStatus(avg, target, targetMax, targetType, key),
        daysGood: series.filter((p) => p.status === 'good').length,
        series,
      };
    }).filter(Boolean) as PeriodNutrient[];
  })();

  return {
    nutrients,
    loading,
    error,
    daysWithData: days.length,
    firstDay: data?.first_day ?? null,
    stats: {
      good: nutrients.filter((n) => n.status === 'good').length,
      warning: nutrients.filter((n) => n.status === 'warning').length,
      lack: nutrients.filter((n) => n.status === 'lack').length,
      total: nutrients.length,
    },
  };
}

export interface PeriodSource {
  name: string;
  amount: number;
  times: number;
  share: number;
}

/** Какие продукты дали больше всего этого нутриента за период. */
export async function fetchNutrientSourcesRange(from: string, to: string, key: NutrientKey): Promise<PeriodSource[]> {
  const { data, error } = await supabase.rpc('get_nutrient_sources_range', { p_from: from, p_to: to, p_key: key });
  if (error) {
    console.error('[fetchNutrientSourcesRange] error:', error);
    return [];
  }
  return ((data as any)?.sources ?? []) as PeriodSource[];
}
